import React, { useRef } from 'react';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _some from 'lodash/some';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _toLower from 'lodash/toLower';
import _includes from 'lodash/includes';
import _reduce from 'lodash/reduce';
import styled, { css } from 'styled-components';
import { Text as P } from 'styles/elements/text';
import { STRINGS } from 'config';
import CompoundAnswer from 'styles/blocks/compound';
import Question from 'styles/blocks/question';
import TextField from 'styles/blocks/textField';
import { ConstructedDateInput as DateInput } from 'styles/blocks/dateInput';
import { splitYear } from 'common/dates';
import { NextButton } from '../nextButton';
import { createMarkup, getIdFromQuestionText } from './compoundBody';
import { SelectFlexible } from '../singleSelect/flexible';

const containerStyles = css`
  margin-bottom: 0;
  flex-direction: row;
`;

const dateContainerStyles = css`
  flex-direction: row;
  label{
    display: none;
  }
  div{
    flex: 1 1 0;
  }
`;

const selectContainerStyles = css`
  flex-direction: row;
  justify-content: space-between;
`;

const optionContainerStyles = count => {
  return css`
    width: ${100 / count}%;
    padding: 0 10px;
    button{
      width: 100%;
    }`;
};

const answerStyleHalfWidth = css`
  width:50%;
  padding: 0 20px;
  margin-bottom: 0;  
`;

const answerStyleFullWidth = css`
  width:100%;
  padding: 0 20px;
  margin-bottom: 0;  
`;

const answerStyleHidden = css`
  display: none;
`;

const Text = styled(P)`
  color: ${props => props.theme.fontColorPrimary};
  margin-top: 15px;
  margin-bottom: 0;
  font-size: ${props => props.theme.fontSizeLarge};
  font-family: ${props => props.theme.fontFamilyLight};
`;

const getInputType = answer => {
  if (answer.tags) {
    if (answer.tags.includes(STRINGS.TAGS.ANSWER.INPUT_TYPE_NUMBER)) {
      return STRINGS.INPUT_TYPES.NUMBER;
    }
    if (answer.tags.includes(STRINGS.TAGS.ANSWER.INPUT_TYPE_EMAIL)) {
      return STRINGS.INPUT_TYPES.EMAIL;
    }
    if (answer.tags.includes(STRINGS.TAGS.ANSWER.INPUT_TYPE_PHONE)) {
      return STRINGS.INPUT_TYPES.PHONE;
    }
  }
  return STRINGS.INPUT_TYPES.TEXT;
};

const generateErrorMessage = (error, subQuestion) => {
  const errorCode = _get(error, 'code', null);
  if (errorCode === STRINGS.VALIDATION_ERROR_CODES.ANSWER_LENGTH_GT_MAX) {
    return `Your answer should be ${subQuestion.maxLength} characters or less`;
  }
  if (errorCode === STRINGS.VALIDATION_ERROR_CODES.ANSWER_LENGTH_LT_MIN) {
    return `Your answer should be ${subQuestion.minLength} characters or more`;
  }
  if (errorCode === STRINGS.VALIDATION_ERROR_CODES.MISSING_ANSWER) {
    return 'This answer cannot be left blank';
  }
  return (error && error.message) || 'Please provide a valid answer';
};

/**
 * copies the list of questions in the compound question and adds dummy (empty) questions after any questions tagged with
 * 'UI_FULL_WIDTH' so that the questions are displayed correctly on the page. Also sets the style property of each question
 * accordingly to one of full width, half width or hidden. Adds a dummy question to the end of the question list if there
 * are an odd number of questions so that the list can be subsequently divided into LH and RH questions for display.
 */
const prepareQuestions = question => {
  const dummyQuestion = { answerStyle: answerStyleHidden };
  const questionsWithStyles = _reduce(question.questions, (result, subQ) => {
    const isFullWidthQ = _includes(subQ.tags, 'UI_FULL_WIDTH');
    const answerStyle = isFullWidthQ ? answerStyleFullWidth : answerStyleHalfWidth;
    const questionWithStyle = { ...subQ, answerStyle };
    return [...result, questionWithStyle, ...(isFullWidthQ ? [dummyQuestion] : [])];
  }, []);
  const isOddNumberOfQuestions = questionsWithStyles.length % 2 === 1;
  return [...questionsWithStyles, ...(isOddNumberOfQuestions ? [dummyQuestion] : [])];
};

/**
 * Bespoke component for displaying the eTriage patient details compound question.
 * This component is used for rendering any compound question that has the tag "STRUCTURED_PATIENT_DETAILS".
 * This renders the questions in a 2-column grid, filling the grid from left to right, ie q1 = top left, q2 = top right,
 * q3 = row 2 left, q4 = row 2 right etc.
 * If a question is tagged with the tag UI_FULL_WIDTH it will be rendered with 100% width and a hidden dummy question
 * will be inserted after it to maintain the intended display order. Such full width questions must be the first question
 * in a row.
 */
export function CompoundPatientDetails({
  question,
  validationErrors,
  compoundAnswers,
  handleUpdateCompoundAnswer,
  isReadOnly,
  isMounted,
  isClinicalReviewMode,
  onClickNextQuestion,
  isNextButtonDisabled,
  showSubmitInsteadOfNext,
}) {
  const inputRef = useRef();
  const questions = prepareQuestions(question);
  const lhQuestions = _filter(questions, (value, index) => (index % 2 === 0));
  const rhQuestions = _filter(questions, (value, index) => (index % 2 === 1));

  function renderUserInput(subQuestion) {
    const isInvalid = _find(validationErrors, error => _some(subQuestion.answers, ['answerId', error.reference]));
    switch (subQuestion.type) {
      case STRINGS.QUESTION_TYPE.FREETEXTSHORT: {
        const inputType = getInputType(subQuestion.answers[0]);
        return (
          <TextField.Input
            aria-label={subQuestion.text}
            value={compoundAnswers[subQuestion.questionId]?.value}
            onChange={event => handleUpdateCompoundAnswer({
              [subQuestion.questionId]: {
                answerId: subQuestion.answers[0].answerId,
                value: event.target.value,
                questionTags: subQuestion.tags,
              },
            })}
            isInvalid={isInvalid}
            type={inputType}
            readOnly={isReadOnly}
            data-automation={`${getIdFromQuestionText(subQuestion.text)}-input`}
            id={`${getIdFromQuestionText(subQuestion.text)}-input`}
          />
        );
      }
      case STRINGS.QUESTION_TYPE.SINGLE: {
        const updateValue = answer => {
          handleUpdateCompoundAnswer({
            [subQuestion.questionId]: {
              answerId: answer.answerId,
              value: answer,
              questionTags: subQuestion.tags,
            },
          });
        };
        return (
          <>
            <CompoundAnswer.AnswersContainer id="container" containerStyles={selectContainerStyles}>
              {_map(subQuestion.answers, (answer, index) => (
                <CompoundAnswer.AnswersContainer id={`option-${index}`} containerStyles={optionContainerStyles(subQuestion.answers.length)}>
                  <SelectFlexible
                    answer={answer}
                    index={index}
                    isMounted={isMounted}
                    isClinicalReviewMode={isClinicalReviewMode}
                    onSelectOption={updateValue}
                    selected={!!_find(compoundAnswers[subQuestion.questionId], { answerId: answer.answerId })}
                    key={`singleSelect-${index}`}
                    secondary={_includes(answer.tags, STRINGS.TAGS.ANSWER.SELECT_SECONDARY)}
                  />
                </CompoundAnswer.AnswersContainer>
              ))}
            </CompoundAnswer.AnswersContainer>
            {subQuestion.guidanceScript && <Question.ETriageHelpLabel dangerouslySetInnerHTML={createMarkup(subQuestion.guidanceScript)} />}
          </>
        );
      }
      case STRINGS.QUESTION_TYPE.DATE: {
        const date = compoundAnswers[subQuestion.questionId]?.value;
        const updateDate = (newDate, dateUnit) => {
          handleUpdateCompoundAnswer({
            [subQuestion.questionId]: {
              answerId: subQuestion.answers[0].answerId,
              value: { ...date, [_toLower(dateUnit[0])]: newDate },
              questionTags: subQuestion.tags,
            },
          });
        };
        return (
          <>
            <CompoundAnswer.AnswersContainer id="container" containerStyles={dateContainerStyles}>
              <DateInput
                day={date?.d}
                month={date?.m}
                year={date?.y}
                onUpdateDate={updateDate}
                isInvalid={isInvalid}
                inputRef={inputRef}
                minYear={splitYear(subQuestion.minDate)}
                maxYear={splitYear(subQuestion.maxDate)}
                isReadOnly={isReadOnly}
              />
            </CompoundAnswer.AnswersContainer>
            {subQuestion.guidanceScript && <Question.ETriageHelpLabel dangerouslySetInnerHTML={createMarkup(subQuestion.guidanceScript)} />}
          </>
        );
      }
      default:
        return <div />;
    }
  }

  return (
    <CompoundAnswer>
      {_map(lhQuestions, (subQ, index) => (
        <CompoundAnswer.AnswersContainer id="container" containerStyles={containerStyles}>
          <CompoundAnswer.Answer
            id="split"
            key={`subQuestion-${index}-l`}
            answerStyles={subQ.answerStyle}
          >
            <Text>
              <Question.ReceivedHTML
                dangerouslySetInnerHTML={createMarkup(`${subQ.text}`)}
              />
            </Text>
            {_map(_filter(validationErrors, error => _some(subQ.answers, ['answerId', error.reference]) || error.reference === subQ.questionId), (error, errorIndex) => (
              <CompoundAnswer.Error
                key={`compound-question-0-error-${errorIndex}`}
                dangerouslySetInnerHTML={createMarkup(generateErrorMessage(error, subQ))}
              />
            ))}
            {renderUserInput(subQ)}
          </CompoundAnswer.Answer>

          <CompoundAnswer.Answer
            id="split"
            key={`subQuestion-${index}-r`}
            answerStyles={rhQuestions[index].answerStyle}
          >
            <Text>
              <Question.ReceivedHTML
                dangerouslySetInnerHTML={createMarkup(`${rhQuestions[index].text}`)}
              />
            </Text>
            {_map(_filter(validationErrors, error => _some(rhQuestions[index].answers, ['answerId', error.reference])
              || error.reference === rhQuestions[index].questionId), (error, errorIndex) => (
                <CompoundAnswer.Error
                  key={`compound-question-0-error-${errorIndex}`}
                  dangerouslySetInnerHTML={createMarkup(generateErrorMessage(error, rhQuestions[index]))}
                />
            ))}
            {renderUserInput(rhQuestions[index])}
          </CompoundAnswer.Answer>
        </CompoundAnswer.AnswersContainer>
      ))}
      <NextButton
        dataAutomation={`nextButton-${question.questionId}`}
        onClick={onClickNextQuestion}
        isDisabled={isNextButtonDisabled}
      >{showSubmitInsteadOfNext && 'Submit'}
      </NextButton>
    </CompoundAnswer>
  );
}
